import { useState } from 'react';
import { Loader2, Search, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function EntityIdDerivationAuditPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.entityIdDerivationAudit();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Entity ID Derivation Audit</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Scans records with both entity_uuid and entity_id, derives the expected entity_id
          from entity_uuid via the canonical registry, and reports disagreements.
        </p>

        <Button onClick={run} disabled={loading} size="sm">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
          Run
        </Button>

        {result && (
          <div className="space-y-4">
            <div className="flex gap-4 text-sm">
              <span>Records checked: <strong>{result.total_checked}</strong></span>
              {result.total_diverged === 0 ? (
                <Badge className="bg-green-100 text-green-800 border-green-300 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> No divergences
                </Badge>
              ) : (
                <Badge variant="destructive" className="flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {result.total_diverged} divergences
                </Badge>
              )}
            </div>

            <p className="text-xs text-muted-foreground">
              Collections: {result.checked_collections?.join(', ')}
            </p>

            {result.divergences?.length > 0 && (
              <div className="overflow-x-auto border rounded">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left px-2 py-1.5 font-medium">Collection</th>
                      <th className="text-left px-2 py-1.5 font-medium">Doc ID</th>
                      <th className="text-left px-2 py-1.5 font-medium">Entity UUID</th>
                      <th className="text-left px-2 py-1.5 font-medium">Stored entity_id</th>
                      <th className="text-left px-2 py-1.5 font-medium">Expected entity_id</th>
                      <th className="text-left px-2 py-1.5 font-medium">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.divergences.map((d, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="px-2 py-1 font-mono">{d.collection}</td>
                        <td className="px-2 py-1 font-mono text-xs truncate max-w-[120px]">{d.doc_id}</td>
                        <td className="px-2 py-1 font-mono text-xs truncate max-w-[120px]">{d.entity_uuid}</td>
                        <td className="px-2 py-1 font-mono text-red-600">{d.stored_entity_id}</td>
                        <td className="px-2 py-1 font-mono text-green-600">{d.expected_entity_id ?? 'null'}</td>
                        <td className="px-2 py-1 text-muted-foreground">{d.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
